// Iter 321 — Previsualizador de partes diarios para el flujo de VB.
// Usado en "Mis VB Supervisor" y en la Bandeja de Aprobaciones.
import { useState, useEffect } from 'react';
import { partesVbApi } from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const horario = (ln) => {
  if (ln.ausencia) return null;
  const b1 = ln.hora_entrada && ln.hora_salida ? `${ln.hora_entrada}–${ln.hora_salida}` : '';
  const b2 = ln.hora_entrada_2 && ln.hora_salida_2 ? `${ln.hora_entrada_2}–${ln.hora_salida_2}` : '';
  return [b1, b2].filter(Boolean).join(' / ') || '—';
};

export const PartePreviewDialog = ({ parteId, open, onOpenChange }) => {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !parteId) return;
    setDetalle(null);
    setLoading(true);
    partesVbApi.detalle(parteId)
      .then((res) => setDetalle(res.data))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Error cargando el parte'))
      .finally(() => setLoading(false));
  }, [open, parteId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="dialog-parte-preview">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-emerald-600" />
            Parte diario {detalle?.fecha || ''}
          </DialogTitle>
          <DialogDescription>
            {detalle && (
              <>
                OT <strong>{detalle.ot_principal || '-'}</strong>
                {detalle.ot_descripcion ? ` · ${detalle.ot_descripcion}` : ''}
                {detalle.cliente_nombre ? ` · ${detalle.cliente_nombre}` : ''}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando parte…
          </div>
        )}

        {!loading && detalle && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
              <span>Supervisor: <span className="text-foreground font-medium">{detalle.supervisor_nombre || '—'}</span></span>
              <span>Preparado por: <span className="text-foreground font-medium">{detalle.prevencionista_nombre || '—'}</span></span>
              {detalle.lugar_almuerzo && <span>Almuerzo: <span className="text-foreground">{detalle.lugar_almuerzo}</span></span>}
              <span>Estado: <Badge variant="outline" className="text-[10px]">{detalle.estado}</Badge></span>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[50vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="parte-preview-table">
                  <thead className="bg-muted sticky top-0">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-2 py-1.5 w-8">#</th>
                      <th className="px-2 py-1.5">Trabajador</th>
                      <th className="px-2 py-1.5 whitespace-nowrap">Horario</th>
                      <th className="px-2 py-1.5 text-center">Alm.</th>
                      <th className="px-2 py-1.5">OTs asignadas</th>
                      <th className="px-2 py-1.5 text-right">HH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalle.personal || []).map((ln, i) => (
                      <tr key={i} className="border-t hover:bg-muted/40">
                        <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          {ln.trabajador_nombre}
                          {ln.observaciones && <div className="text-[10px] text-muted-foreground italic">{ln.observaciones}</div>}
                        </td>
                        <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                          {ln.ausencia
                            ? <Badge variant="destructive" className="text-[10px]">{ln.ausencia}</Badge>
                            : horario(ln)}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {ln.almuerzo ? (ln.tipo_almuerzo || 'Sí') : '—'}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex flex-wrap gap-1">
                            {(ln.asignaciones || []).map((a, j) => (
                              <Badge key={j} variant="secondary" className="text-[10px] font-mono">
                                OT {a.ot_numero} · {a.horas}h
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold">{ln.total_horas || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/60 border-t sticky bottom-0">
                    <tr className="font-semibold">
                      <td colSpan={5} className="px-2 py-1.5 text-right">
                        {(detalle.personal || []).length} personas · Total HH
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono" data-testid="parte-preview-total-hh">{detalle.total_hh}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
